import { useState } from 'react';
import { ChevronRight, TriangleAlert } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardSubtitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/cn';
import type { TrendClient, TrendTarget, TrendWindow } from '@/api/dashboardTrends';
import {
    TREND_WINDOWS,
    classifyTrend,
    formatDelta,
    formatValue,
    summarizeClient,
    type TrendStatus,
} from './trends';

/*
 * Caseload trends gadget — one collapsed row per assigned client with
 * improving/flat/declining counts, expanding to program → target rows with
 * the window-vs-window delta. Exception-first: the collapsed summary answers
 * "which of my clients need attention"; the full per-target graph lives in
 * analytics.
 */

const STATUS_META: Record<TrendStatus, { label: string; className: string }> = {
    improving:    { label: 'Improving',      className: 'text-success-text' },
    declining:    { label: 'Declining',      className: 'text-error-text' },
    flat:         { label: 'Flat',           className: 'text-text-muted' },
    baseline:     { label: 'New',            className: 'text-text-muted' },
    insufficient: { label: 'No recent data', className: 'text-text-subtle' },
};

function TargetRow({ target }: { target: TrendTarget }) {
    const verdict = classifyTrend(target);
    const meta = STATUS_META[verdict.status];
    const isTrend = verdict.delta !== null;
    const cur = target.current.avg;

    return (
        <li className="flex items-center justify-between gap-3 py-1">
            <span className="text-sm text-text truncate">{target.name}</span>
            <span className="flex items-center gap-2 shrink-0">
                {/* Trend: prev → cur. Baseline: just the current level. */}
                {isTrend && target.previous.avg !== null && cur !== null ? (
                    <span className="text-xs tabular-nums text-text-muted">
                        {formatValue(target.dataType, target.previous.avg)}
                        {' → '}
                        {formatValue(target.dataType, cur)}
                    </span>
                ) : verdict.status === 'baseline' && cur !== null ? (
                    <span className="text-xs tabular-nums text-text-muted">
                        {formatValue(target.dataType, cur)}
                    </span>
                ) : null}
                <span className={cn('text-xs font-medium whitespace-nowrap flex items-center gap-1', meta.className)}>
                    {isTrend && (
                        <span className="tabular-nums">{formatDelta(target.dataType, verdict.delta!)} · </span>
                    )}
                    {meta.label}
                    {verdict.limited && (
                        <TriangleAlert
                            className="w-3.5 h-3.5 text-warning-text"
                            role="img"
                            aria-label="Based on limited data — one session in a comparison period"
                        >
                            <title>Based on limited data — one session in a comparison period</title>
                        </TriangleAlert>
                    )}
                </span>
            </span>
        </li>
    );
}

function ClientRow({ client, defaultExpanded }: { client: TrendClient; defaultExpanded: boolean }) {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const summary = summarizeClient(client);

    const chips: { text: string; className: string }[] = [];
    if (summary.improving) chips.push({ text: `${summary.improving} improving`, className: 'text-success-text' });
    if (summary.declining) chips.push({ text: `${summary.declining} declining`, className: 'text-error-text font-medium' });
    if (summary.flat) chips.push({ text: `${summary.flat} flat`, className: 'text-text-muted' });
    if (summary.baseline) chips.push({ text: `${summary.baseline} new`, className: 'text-text-muted' });
    if (summary.insufficient) chips.push({ text: `${summary.insufficient} no data`, className: 'text-text-subtle' });

    return (
        <li>
            <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                aria-expanded={expanded}
                className="w-full flex items-center justify-between gap-3 py-2.5 text-left rounded hover:bg-surface-muted transition-colors duration-[var(--duration-base)]"
            >
                <span className="flex items-center gap-1.5 min-w-0">
                    <ChevronRight
                        className={cn(
                            'w-4 h-4 shrink-0 text-text-subtle transition-transform duration-[var(--duration-base)]',
                            expanded && 'rotate-90',
                        )}
                        aria-hidden
                    />
                    <span className="text-sm font-medium text-text truncate">{client.name}</span>
                </span>
                <span className="text-xs shrink-0">
                    {chips.map((chip, i) => (
                        <span key={chip.text} className={chip.className}>
                            {i > 0 && <span className="text-text-subtle font-normal"> · </span>}
                            {chip.text}
                        </span>
                    ))}
                </span>
            </button>
            {expanded && (
                <div className="pl-6 pb-3 flex flex-col gap-2">
                    {client.programs.map((program) => (
                        <div key={program.id}>
                            <p className="text-xs font-semibold text-text-subtle mb-0.5">{program.name}</p>
                            <ul>
                                {program.targets.map((target) => (
                                    <TargetRow key={target.id} target={target} />
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </li>
    );
}

export function ClientTrendsGadget({
    clients,
    windowDays,
    onWindowChange,
    isLoading,
}: {
    clients: TrendClient[] | undefined;
    windowDays: TrendWindow;
    onWindowChange: (w: TrendWindow) => void;
    isLoading: boolean;
}) {
    const windowLabel = TREND_WINDOWS.find((w) => w.value === windowDays)?.label ?? '';

    return (
        <Card>
            <CardHeader className="items-start">
                <div className="flex flex-col gap-0.5">
                    <CardTitle>Client trends</CardTitle>
                    <CardSubtitle>
                        Average change vs the previous {windowLabel}, by session date.
                        A ⚠ marks trends resting on a single session.
                    </CardSubtitle>
                </div>
                <fieldset>
                    <legend className="sr-only">Comparison window</legend>
                    <div className="flex rounded-md border border-border overflow-hidden">
                        {TREND_WINDOWS.map((w) => (
                            <label
                                key={w.value}
                                className={cn(
                                    'px-2.5 py-1 text-xs cursor-pointer whitespace-nowrap focus-within:ring-2 focus-within:ring-accent-dark',
                                    w.value === windowDays
                                        ? 'bg-accent font-medium text-text'
                                        : 'bg-surface text-text-muted hover:bg-surface-muted',
                                )}
                            >
                                <input
                                    type="radio"
                                    name="trend-window"
                                    className="sr-only"
                                    checked={w.value === windowDays}
                                    onChange={() => onWindowChange(w.value)}
                                />
                                {w.label}
                            </label>
                        ))}
                    </div>
                </fieldset>
            </CardHeader>

            {isLoading && !clients ? (
                <div className="flex flex-col gap-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} className="h-8 w-full" />
                    ))}
                </div>
            ) : !clients || clients.length === 0 ? (
                <p className="text-sm text-text-subtle py-4 text-center">
                    No active targets for your assigned clients yet.
                </p>
            ) : (
                <ul className="divide-y divide-border">
                    {clients.map((client) => (
                        <ClientRow
                            key={client.id}
                            client={client}
                            defaultExpanded={clients.length === 1}
                        />
                    ))}
                </ul>
            )}
        </Card>
    );
}
